import { EditableSnapQuote } from '@snap-types/actions/edit-snap-quote';

import { QuoteType } from '../../types/state/quote';
import { QuoteLineType } from '../../types/state/quote-line';
import { transformSnapQuoteLineToStoreState } from './transform-quote-line';

export function transformSnapQuoteToStoreState(quote: EditableSnapQuote): {
  quote: QuoteType;
  quoteLineUuidsWithoutLastWins: string[];
} {
  const quoteLineUuids: string[] = [];
  const quoteLines: Record<string, QuoteLineType> = {};
  const quoteLineUuidsWithoutLastWins: string[] = [];

  for (const snapQuoteLine of quote.latestVersion.snapQuoteLines) {
    const { uuid, quoteLine, hasLastWin } = transformSnapQuoteLineToStoreState(
      snapQuoteLine,
      quote.site.siteQuotingConfig.calculationConfig,
    );

    quoteLineUuids.push(uuid);
    quoteLines[uuid] = quoteLine;

    if (!hasLastWin) {
      quoteLineUuidsWithoutLastWins.push(uuid);
    }
  }

  return {
    quoteLineUuidsWithoutLastWins,
    quote: {
      editMode: quote.versions.length > 1 ? 'edit' : 'new',
      uuid: quote.uuid,
      versionUuid: quote.latestVersion.uuid,
      versionCount: quote.versions.length,
      sentAt: quote.lastSentAt,
      quoteId: quote.quoteId,
      siteCode: quote.site.code,

      customerId: quote.customer?.customerId,
      termsNetDays: quote.customer?.termsNetDays,
      displayQuantity: quote.site.siteQuotingConfig.quantityDisplayMagnitude,
      priceStrategies: quote.site.siteQuotingConfig.priceStrategies,
      calculationConfig: quote.site.siteQuotingConfig.calculationConfig,
      leadTimeFormat: quote.site.siteQuotingConfig.leadTimeFormat,
      discountCodes: quote.site.discountCodes,
      marketCodes: quote.site.marketCodes,
      wonLossStatus: quote.latestVersion.wonLossStatus,

      name: quote.latestVersion.name,
      contactName: quote.latestVersion.contactName,
      contactEmail: quote.latestVersion.contactEmail,
      contactPhone: quote.latestVersion.contactPhone,
      customerContacts: quote.customer?.contacts,
      address1: quote.latestVersion.address1,
      address2: quote.latestVersion.address2,
      city: quote.latestVersion.city,
      state: quote.latestVersion.state,
      postalCode: quote.latestVersion.zipcode,
      countryCode: quote.latestVersion.country,

      customerQuoteId: quote.latestVersion.customerQuoteId,
      notes: quote.latestVersion.notes,
      specs: quote.latestVersion.specs,

      salesRepId: quote.latestVersion.salesRepId,
      salesRepName: quote.latestVersion.salesRepName,
      salesRepEmail: quote.latestVersion.salesRepEmail,
      salesRepPhone: quote.latestVersion.salesRepPhone,

      csrOid: quote.latestVersion.csrOid,
      csrName: quote.latestVersion.csrName,
      csrEmail: quote.latestVersion.csrEmail,

      daysUntilExpectedWin: quote.latestVersion.daysUntilExpectedWin,
      daysUntilQuoteExpires: quote.latestVersion.daysUntilQuoteExpires,
      quantityRange: quote.site.siteQuotingConfig.quantityRange,

      discountCode: quote.latestVersion.discountCode,
      marketCode: quote.latestVersion.marketCode,
      targetGrossMargin: quote.site.siteQuotingConfig.targetGrossMargin,
      targetContributionMargin:
        quote.site.siteQuotingConfig.targetContributionMargin,

      quoteLineUuids,
      quoteLines,
    },
  };
}

export function transformStoreStateToNestedQuoteLinesAndPrices(
  quoteLinesState: Record<string, QuoteLineType> | undefined,
) {
  if (!quoteLinesState) {
    return [];
  }

  return Object.values(quoteLinesState).map((line) => {
    const quotePrices = Object.values(line.quotePrices).map((quotePrice) => ({
      quantity: quotePrice.quantity,
      totalPrice: quotePrice.totalPrice,
    }));

    return {
      snapQuotePrices: quotePrices,
    };
  });
}
